/**
 * CalendarClient.ts
 *
 * CalDAV 서버와의 연결을 관리하는 클라이언트 모듈
 *
 * 주요 기능:
 * - CalDAV 클라이언트 초기화 및 설정
 * - 사용자 인증 및 로그인/로그아웃 처리
 * - 클라이언트 인스턴스 관리
 * - 설정 변경 감지 및 처리
 *
 * 이 모듈은 CalDAV 프로토콜을 통해 캘린더 서버와의 모든 통신을 담당합니다.
 */

import { DAVClient } from 'libs/tsdav'
import conf from 'conf'
import settings from 'popups/Settings'
import { classInfo, userInfo, instanceEmitter } from './CalendarState'
import { fetchCalendars } from './CalendarSync'
import { _showHiddenEvents } from './EventInstanceManager'
import { getEnvParam } from 'env'

interface MyInfo {
  email: string
  display_name?: string
  name?: string
}

interface CalendarSettings {
  hideDeclinedMeeting: boolean
  hideCanceledMeeting: boolean
}

// 서버 URL 설정
const calDavServerUrl = (conf.SERVER.davURL ?? conf.SERVER.BASE_URL) + '/dav/calendars'

let client: DAVClient | null = null
let oldSettings: CalendarSettings | null = null
let clientInitPromise: Promise<void> | null = null
let settingsSubscribed = false

/**
 * Logout and clear all calendar data
 */
export const logout = async (): Promise<void> => {
  // ICS 구독 auto-refresh 타이머 정리
  const { stopAutoRefresh } = await import('./subscription')
  stopAutoRefresh()

  client = null
  Object.keys(userInfo).forEach((key) => {
    delete (userInfo as any)[key]
  })
  classInfo.calendars = null
  classInfo.instances = []
  classInfo.notifications = []
}

/**
 * Initialize CalDAV client with user information
 * @param myInfo - User information object containing email and name
 */
export const initClient = async (myInfo: MyInfo | null): Promise<void> => {
  // 이미 초기화 진행 중이면 기존 Promise를 대기하여 race condition 방지
  // iOS WebView에서는 멀티 쓰레드로 동작하는 문제 발생.
  if (clientInitPromise) {
    return clientInitPromise
  }

  clientInitPromise = (async () => {
    console.log('init calendar client')
    if (myInfo != null) {
      userInfo.email = myInfo.email
      userInfo.name = myInfo.display_name || myInfo.name
      if (!userInfo.name || userInfo.name.length === 0) {
        userInfo.name = userInfo.email.split('@')[0]
      }

      const userInternalEmail = getEnvParam('userInternalEmail') as string | null
      if (userInternalEmail && userInternalEmail !== userInfo.email) {
        userInfo.personaEmail = userInternalEmail
        userInfo.personaDomain = userInternalEmail.split('@')[1]
      } else {
        userInfo.personaEmail = null
        userInfo.personaDomain = null
      }
    }

    // DAVClient 생성
    client = new DAVClient({
      serverUrl: calDavServerUrl,
      credentials: {} as any,
      authMethod: 'Custom',
      authFunction: async (_credentials: any) => {
        // const expiresIn = await localForage.getItem('app.user.refreshExpiresIn')
        // if (expiresIn != null && expiresIn * 1000 < Date.now()) {
        //   await updateToken()
        // }

        // const accessToken = await localForage.getItem('app.user.token')
        // // dav/calenders는 Bearer type으로 호출해야 함. tokenType 고정.
        // const tokenType = 'Bearer' // await localForage.getItem('app.user.tokenType')
        // return {
        //   Authorization: `${tokenType} ${accessToken}`,
        // }
        return {}
      },
      defaultAccountType: 'caldav',
    })

    // 인증 및 로그인
    try {
      await client.login()
      await fetchCalendars(true)

      // ICS 구독 캘린더 복원 및 auto-refresh 시작
      const { initSubscriptions } = await import('./subscription')
      initSubscriptions()
    } catch (reason) {
      console.error('Calendar initClient Failed : %o', reason)
    }

    // settings subscribe는 한 번만 등록
    if (!settingsSubscribed) {
      settingsSubscribed = true
      oldSettings = settings.getValue('calendar') as CalendarSettings
      settings.getStore().subscribe(() => {
        const newSettings = settings.getValue('calendar') as CalendarSettings
        const newHideDeclined = newSettings.hideDeclinedMeeting
        const newHideCanceled = newSettings.hideCanceledMeeting
        const oldHideDeclined = oldSettings!.hideDeclinedMeeting
        const oldHideCanceled = oldSettings!.hideCanceledMeeting

        if (oldHideDeclined !== newHideDeclined || oldHideCanceled !== newHideCanceled) {
          if ((oldHideDeclined && !newHideDeclined) || (oldHideCanceled && !newHideCanceled)) {
            _showHiddenEvents()
          }

          if ((!oldHideDeclined && newHideDeclined) || (!oldHideCanceled && newHideCanceled)) {
            // Clear canceled or declined events.
            classInfo.instances = classInfo.instances.filter(
              (i) => !((newHideCanceled && i.isCanceled) || (newHideDeclined && i.isDeclined)),
            )
            instanceEmitter.emit('instance')
          }

          oldSettings = newSettings
        }
      })
    }
  })()

  try {
    await clientInitPromise
  } finally {
    clientInitPromise = null
  }
}

/**
 * Get the current DAVClient instance
 * @returns The DAVClient instance or null if not initialized
 */
export const getClient = (): DAVClient | null => {
  return client
}
